'use client'

import ConfirmModal from '@/components/confirm-modal'
import { usePermission } from '@/lib/hooks/use-permission'
import {
    createSubPlanRegistrationLink,
    getSubPlans,
    removeSubPlan,
    SubPlan,
} from '@/lib/api/resales/resales-api'
import { copyTextLazy } from '@/lib/utils/clipboard'
import { useCallback, useEffect, useState } from 'react'
import {
    MdAdd,
    MdDelete,
    MdEdit,
    MdGroups,
    MdLink,
    MdStorefront,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number | null | undefined) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format((cents || 0) / 100)

const recurrenceLabel = (recurrence: string | null) =>
    recurrence === 'YEARLY' ? '/ano' : '/mês'

export default function ResalesListPage() {
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = usePermission()
    const canCreateResale = canCreate('administrative', 'resale')
    const canUpdateResale = canUpdate('administrative', 'resale')
    const canDeleteResale = canDelete('administrative', 'resale')

    const [plans, setPlans] = useState<SubPlan[]>([])
    const [loading, setLoading] = useState(true)
    const [deleteTarget, setDeleteTarget] = useState<SubPlan | null>(null)
    const [deleting, setDeleting] = useState(false)
    const [forbidden, setForbidden] = useState(false)
    // `${planId}:${type}` do link sendo copiado (para feedback no botão).
    const [copying, setCopying] = useState<string | null>(null)

    /**
     * Gera um link NOVO de uso único e copia. Os links já enviados a outros
     * clientes continuam valendo — por isso cada clique aqui cria mais um, em
     * vez de reaproveitar (e queimar) um link em circulação.
     */
    const copyLink = async (planId: number, type: 'pj' | 'pf') => {
        const key = `${planId}:${type}`
        setCopying(key)
        try {
            // O link é criado dentro do callback para preservar a "user
            // activation" do clique — um await de rede antes da escrita no
            // clipboard a invalidaria e o navegador bloquearia a cópia.
            const copied = await copyTextLazy(async () => {
                const res = await createSubPlanRegistrationLink(planId)
                if (!res.success || !res.result?.token) {
                    throw new Error('Falha ao gerar o link de cadastro')
                }
                return `${window.location.origin}/register?type=${type}&token=${res.result.token}`
            })
            if (copied) {
                toast.success(
                    `Novo link ${type.toUpperCase()} gerado e copiado!`,
                )
            } else {
                toast.error('Erro ao copiar o link')
            }
        } catch {
            toast.error('Erro ao copiar o link')
        } finally {
            setCopying(null)
        }
    }

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const response = await getSubPlans()
            if (response.success && response.result) {
                setPlans(response.result)
            } else if (response.error) {
                // Plano sem permissão de revenda
                setForbidden(true)
            }
        } catch {
            setForbidden(true)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const handleDelete = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        try {
            const response = await removeSubPlan(deleteTarget.id)
            if (response.success) {
                toast.success('Plano removido')
                setDeleteTarget(null)
                load()
            } else {
                toast.error(response.error || 'Erro ao remover plano')
            }
        } catch {
            toast.error('Erro ao remover plano')
        } finally {
            setDeleting(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando planos...
                </p>
            </div>
        )
    }

    if (forbidden) {
        return (
            <div className="bg-base-100 rounded-xl border border-base-300/50 p-10 flex flex-col items-center text-center gap-3">
                <MdStorefront className="text-4xl text-base-content/40" />
                <h2 className="text-lg font-semibold">
                    Revenda não disponível
                </h2>
                <p className="text-sm text-base-content/60 max-w-md">
                    Seu plano não habilita a revenda. Fale com o suporte para
                    liberar a criação de planos.
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdStorefront className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-base-content">
                            Revenda
                        </h1>
                        <p className="text-sm text-base-content/60">
                            Crie planos e use o link de cadastro do plano
                        </p>
                    </div>
                </div>
                {canCreateResale && (
                    <button
                        className="btn btn-secondary gap-2"
                        onClick={() => navigate('/administrative/resales/new')}
                    >
                        <MdAdd className="text-lg" />
                        Novo plano
                    </button>
                )}
            </div>

            {plans.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-10 flex flex-col items-center text-center gap-3">
                    <MdStorefront className="text-4xl text-base-content/40" />
                    <h2 className="text-lg font-semibold">
                        Nenhum plano ainda
                    </h2>
                    <p className="text-sm text-base-content/60 max-w-md">
                        Crie um plano para começar a revender. Você poderá
                        gerar um link de cadastro para cada cliente — vários links podem
                        circular ao mesmo tempo.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {plans.map(plan => (
                        <div
                            key={plan.id}
                            className="bg-base-100 rounded-xl border border-base-300/50 p-5 flex flex-col gap-3"
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <h3 className="font-semibold text-base-content truncate">
                                        {plan.name}
                                    </h3>
                                    <p className="text-xs text-base-content/50 line-clamp-2">
                                        {plan.description}
                                    </p>
                                </div>
                                <span className="text-lg font-bold text-secondary whitespace-nowrap">
                                    {formatCurrency(plan.price)}
                                    <span className="text-xs text-base-content/50 font-normal">
                                        {recurrenceLabel(plan.recurrence)}
                                    </span>
                                </span>
                            </div>

                            <div className="flex items-center gap-1 text-xs text-base-content/60">
                                <MdGroups className="text-sm" />
                                {plan._count?.subscriptions ?? 0} assinante(s)
                            </div>

                            {/* Gerar e copiar um link de cadastro novo. Cada
                                clique cria um link de uso único — os anteriores
                                seguem valendo. */}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-xs btn-outline btn-secondary flex-1 gap-1"
                                    onClick={() => copyLink(plan.id, 'pj')}
                                    disabled={copying === `${plan.id}:pj`}
                                    title="Gerar e copiar um novo link de cadastro PJ"
                                >
                                    {copying === `${plan.id}:pj` ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <MdLink className="text-sm" />
                                    )}
                                    PJ
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-xs btn-outline btn-secondary flex-1 gap-1"
                                    onClick={() => copyLink(plan.id, 'pf')}
                                    disabled={copying === `${plan.id}:pf`}
                                    title="Gerar e copiar um novo link de cadastro PF"
                                >
                                    {copying === `${plan.id}:pf` ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <MdLink className="text-sm" />
                                    )}
                                    PF
                                </button>
                            </div>

                            <div className="flex gap-2 pt-1">
                                {canUpdateResale && (
                                    <button
                                        className="btn btn-sm btn-outline flex-1 gap-1"
                                        onClick={() =>
                                            navigate(
                                                `/administrative/resales/${plan.id}`
                                            )
                                        }
                                    >
                                        <MdEdit className="text-base" />
                                        Gerenciar
                                    </button>
                                )}
                                {canDeleteResale && (
                                    <button
                                        className="btn btn-sm btn-ghost text-error"
                                        onClick={() => setDeleteTarget(plan)}
                                    >
                                        <MdDelete className="text-base" />
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remover plano"
                message={`Tem certeza que deseja remover o plano "${deleteTarget?.name}"? Assinantes existentes não são afetados, mas o link de cadastro deixa de funcionar.`}
                loading={deleting}
                confirmButtonText="Remover"
                confirmButtonClass="btn-error"
            />
        </div>
    )
}
